import { useNavigate } from "react-router-dom";
import {
  Wrench,
  Drill,
  HardHat,
  Cog,
  Users,
  Truck,
  ShieldCheck,
  Package,
  ArrowRight,
} from "lucide-react";

import rigBg from "../../assets/images/offshore_drilling_operations.jpg";
import MotionReveal from "../common/MotionReveal/MotionReveal";
import { ROUTES, SERVICE_ROUTES } from "../../constants/routes";

function ServicesPreview() {
  const navigate = useNavigate();

  const services = [
    {
      title: "Operations & Maintenance",
      icon: Cog,
      description:
        "Integrated maintenance, asset reliability, and operational continuity support.",
      path: SERVICE_ROUTES.om,
      ctaLabel: "View O&M",
    },
    {
      title: "Drilling Support Services",
      icon: Drill,
      description:
        "Operational drilling support for offshore and onshore energy programs.",
      path: SERVICE_ROUTES.drilling,
      ctaLabel: "View Drilling",
    },
    {
      title: "Workover & Well Intervention",
      icon: Wrench,
      description:
        "Specialized intervention and completion services to restore and optimize wells.",
      path: SERVICE_ROUTES.workover,
      ctaLabel: "View Workover",
    },
    {
      title: "Global Workforce Solutions",
      icon: Users,
      description:
        "Operator-driven workforce planning, mobilization, and crew support solutions.",
      path: ROUTES.GWS,
      ctaLabel: "View GWS",
    },
    {
      title: "HSEQ Management",
      icon: ShieldCheck,
      description:
        "Safety, quality, environmental, and compliance systems built for energy operations.",
      path: ROUTES.HSEQ,
      ctaLabel: "View HSEQ",
    },
    {
      title: "Awards and Recognitions",
      icon: Package,
      description:
        "Certifications, operational achievements, and industry recognitions in one place.",
      path: ROUTES.AWARDS_AND_RECOGNITIONS,
      ctaLabel: "View Awards and Recognitions",
    },
    {
      title: "Project Experience",
      icon: HardHat,
      description:
        "Explore offshore and onshore project execution across India and global markets.",
      path: ROUTES.PROJECTS,
      ctaLabel: "View Projects",
    },
    {
      title: "All Services Overview",
      icon: Truck,
      description:
        "Browse SAC's full service portfolio across drilling, workover, and O&M.",
      path: ROUTES.SERVICES,
      ctaLabel: "View Services",
    },
  ];

  return (
    <section className="relative overflow-hidden py-16 lg:py-20">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-[0.55]"
        style={{ backgroundImage: `url(${rigBg})` }}
      />
      <div className="absolute inset-0 bg-white/60" />
      <div className="pointer-events-none absolute top-0 right-0 h-[500px] w-[500px] rounded-full bg-red-500/5 blur-[150px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[400px] w-[400px] rounded-full bg-[#041E42]/5 blur-[140px]" />

      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-8">
        <div className="grid items-end gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 xl:gap-16">
          <MotionReveal variant="fadeUp">
            <span className="inline-flex items-center gap-4">
              <span className="h-[2px] w-10 rounded-full bg-red-600" />
              <span className="text-xs font-semibold uppercase tracking-[0.45em] text-red-600">
                What We Do
              </span>
            </span>

            <h2 className="mt-6 text-4xl font-bold leading-[0.98] tracking-tight text-[#041E42] sm:text-5xl lg:text-[3.4rem] xl:text-[3.75rem]">
              Comprehensive Solutions Across the{" "}
              <span className="text-red-600">Operational Lifecycle</span>
            </h2>
          </MotionReveal>

          <MotionReveal variant="fadeUp" delay={100}>
            <p className="text-base leading-8 text-slate-600 text-justify sm:text-lg lg:max-w-xl lg:justify-self-end">
              South Asia Consultancy delivers comprehensive solutions covering
              the complete operational lifecycle of energy assets. By integrating
              operational expertise with workforce excellence, SAC provides
              clients with a single trusted partner capable of supporting
              diverse project requirements across multiple regions and operational
              environments.
            </p>

            <button
              type="button"
              onClick={() => navigate("/services")}
              className="group mt-8 inline-flex items-center gap-5 font-semibold text-[#041E42] lg:justify-self-end"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600 shadow-lg transition duration-300 group-hover:scale-110">
                <ArrowRight
                  size={20}
                  className="text-white transition-transform duration-300 group-hover:translate-x-1"
                />
              </span>
              <span className="text-lg transition-colors group-hover:text-red-600">
                Explore Our Services
              </span>
            </button>
          </MotionReveal>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 lg:mt-12">
          {services.map((service, index) => {
            const Icon = service.icon;

            return (
              <MotionReveal
                key={service.title}
                variant="fadeUp"
                delay={index * 50}
                as="button"
                type="button"
                onClick={() => navigate(service.path)}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white/90 p-4 text-left shadow-[0_8px_24px_rgba(15,23,42,0.05)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-red-200 hover:shadow-[0_14px_32px_rgba(15,23,42,0.08)] sm:p-5"
              >
                <div className="absolute left-0 top-0 h-[3px] w-0 bg-red-600 transition-all duration-300 group-hover:w-full" />

                <div className="flex items-start justify-between gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-100 bg-slate-50 transition-colors duration-300 group-hover:border-[#041E42] group-hover:bg-[#041E42]">
                    <Icon
                      size={20}
                      strokeWidth={1.8}
                      className="text-red-600 transition-colors duration-300 group-hover:text-white"
                    />
                  </div>

                  <span className="text-[11px] font-bold tabular-nums text-slate-200 transition-colors duration-300 group-hover:text-red-100">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                </div>

                <h3 className="mt-3 text-[15px] font-bold leading-snug text-[#041E42] transition-colors duration-300 group-hover:text-red-600">
                  {service.title}
                </h3>

                <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-slate-500">
                  {service.description}
                </p>

                <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
                  <span className="truncate text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 transition-colors duration-300 group-hover:text-red-500">
                    {service.ctaLabel}
                  </span>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 transition-all duration-300 group-hover:bg-red-600">
                    <ArrowRight
                      size={14}
                      className="text-[#041E42] transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-white"
                    />
                  </span>
                </div>
              </MotionReveal>
            );
          })}
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-0 left-0 h-48 w-full bg-gradient-to-t from-white to-transparent" />
    </section>
  );
}

export default ServicesPreview;
